import { useEffect } from 'react';
import type { AppSettings } from '@/access/settings.types';

/**
 * ה manifest הסטטי ב public מכיל ברירות מחדל בלי שם לקוח.
 * כאן דורסים אותו בזמן ריצה מתוך app_settings, כדי ששינוי שם, צבע או אייקון
 * במסך ההגדרות ישפיע על ההתקנה בלי פריסה מחדש (PRD, קריטריוני קבלה "מוצר לשכפול").
 *
 * הערה: דפדפן שכבר התקין את האפליקציה קורא את ה manifest מחדש בהזדמנות הבאה,
 * ולכן האייקון מתעדכן אצל מי שמתקין מכאן והלאה.
 */
export function useRuntimeManifest(settings: AppSettings, isReady: boolean) {
  useEffect(() => {
    if (!isReady) return;

    const link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
    if (!link) return;

    let cancelled = false;
    let objectUrl: string | null = null;

    void (async () => {
      try {
        const response = await fetch('/manifest.webmanifest');
        const base = (await response.json()) as Record<string, unknown>;
        if (cancelled) return;

        const { brand } = settings;
        const baseIcons = (base.icons as Array<Record<string, unknown>>) ?? [];
        const icons = baseIcons.map((icon) => {
          if (icon.sizes === '192x192' && brand.icon_192) return { ...icon, src: brand.icon_192 };
          if (icon.sizes === '512x512' && brand.icon_512) return { ...icon, src: brand.icon_512 };
          return icon;
        });

        const next = {
          ...base,
          name: brand.app_title,
          short_name: brand.name || brand.app_title,
          theme_color: brand.primary_color ?? base.theme_color,
          icons,
        };

        objectUrl = URL.createObjectURL(
          new Blob([JSON.stringify(next)], { type: 'application/manifest+json' }),
        );
        link.href = objectUrl;
      } catch {
        /* ה manifest הסטטי נשאר בתוקף. */
      }
    })();

    const themeMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (themeMeta && settings.brand.primary_color) {
      themeMeta.content = settings.brand.primary_color;
    }

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [settings, isReady]);
}
